import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services';

type LandingPageGroupDto = {
  id: number;
  name: string;
  status?: number;
  domain?: string;
  lastEdited?: string;
  zuulUser?: unknown;
  default?: boolean;
  active?: boolean;
};

export const useLandingPageGroups = () =>
  useQuery<LandingPageGroupDto[], Error>({
    queryKey: ['landingPageGroups'],
    queryFn: () => api.get(`landing-page-groups/all`).then((res) => res.data as LandingPageGroupDto[]),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useUpdateLandingPageGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (landingPageGroup: LandingPageGroupDto) => api.put(`landing-page-groups/${landingPageGroup.id}`, landingPageGroup),
    {
      onSuccess: () => {
        // An array, not a bare string: react-query 4 reads a non-array first argument as a
        // filters object, so this matched every query in the cache instead of this one.
        queryClient.invalidateQueries(['landingPageGroups']);
      },
    }
  );
};

export const useLandingPageGroupsDtos = <T = LandingPageGroupDto[]>(selectFn?: (data: LandingPageGroupDto[]) => T) => {
  const queryOptions: {
    queryKey: string[];
    queryFn: () => Promise<LandingPageGroupDto[]>;
    refetchOnWindowFocus: false;
    refetchOnReconnect: false;
    select?: (data: LandingPageGroupDto[]) => T;
  } = {
    queryKey: ['landingPageGroupsDtos'],
    queryFn: () => api.get(`landing-page-groups/dtos/all`).then((res) => res.data as LandingPageGroupDto[]),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  if (selectFn) {
    queryOptions.select = selectFn;
  }

  return useQuery<LandingPageGroupDto[], Error, T>(queryOptions);
};
